import { standardiseVehicleId } from './vehicleIdStandardise';
import type { ProfitabilityAssumptionsConfig } from '../types/profitabilityTruth';

export interface MergedCostStandingRow {
  date: string;
  vehicle: string;
  revenue?: number;
  daily_miles?: number;
  driving_hours?: number;
  waiting_hours?: number;
  depreciation?: unknown;
}

export interface StandingCostResolution {
  standingTotal: number;
  chargedVehicleDays: number;
  averagePerDay: number | null;
  source: 'merged_depreciation' | 'config_estimate';
  policy: 'calendar_day' | 'active_day';
  rateSummary: string;
}

function parseDepreciation(row: MergedCostStandingRow): number {
  const raw = row.depreciation;
  if (raw == null || raw === '') return 0;
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw));
  return Number.isFinite(n) ? n : 0;
}

function vehicleDayKey(row: MergedCostStandingRow): string | null {
  const d = (row.date || '').slice(0, 10);
  const v = standardiseVehicleId(row.vehicle) || (row.vehicle || '').trim();
  if (!d || !v) return null;
  return `${v}|${d}`;
}

function rowHasActivity(row: MergedCostStandingRow): boolean {
  return (
    (row.revenue ?? 0) > 0 ||
    (row.daily_miles ?? 0) > 0 ||
    (row.driving_hours ?? 0) > 0 ||
    (row.waiting_hours ?? 0) > 0
  );
}

function resolveDepreciationRate(
  vehicleType: string,
  depreciation: Record<string, number>,
): number {
  const t = (vehicleType || '').trim();
  if (depreciation[t] != null) return depreciation[t];
  const lower = t.toLowerCase();
  for (const [key, value] of Object.entries(depreciation)) {
    if (key.toLowerCase() === lower) return value;
  }
  return depreciation.Default ?? depreciation.default ?? 0;
}

export function depreciationRatesFromTruthConfig(
  config: ProfitabilityAssumptionsConfig | null | undefined,
): Record<string, number> {
  if (!config) return { Default: 0 };
  if (config.cost_rate_basis === 'tonnage' && config.depreciation_tonnage) {
    return {
      ...config.depreciation_tonnage,
      Default: config.depreciation_tonnage.Default ?? 0,
    };
  }
  if (!config.depreciation) return { Default: 0 };
  return { ...config.depreciation, Default: config.depreciation.Default ?? 0 };
}

/** Build rate summary for confidence copy (shows min–max or single default). */
export function summariseDepreciationRates(depreciation: Record<string, number>): string {
  const values = Object.entries(depreciation)
    .filter(([k]) => k !== 'Default' && k !== 'default')
    .map(([, v]) => v)
    .filter((v) => Number.isFinite(v) && v > 0);
  const uniq = [...new Set(values)].sort((a, b) => a - b);
  if (uniq.length === 0) {
    const d = depreciation.Default ?? depreciation.default;
    return d != null && d > 0 ? `Default £${d}/vehicle-day` : 'per vehicle type from assumptions';
  }
  if (uniq.length === 1) return `£${uniq[0]}/vehicle-day`;
  return `£${uniq[0]}–£${uniq[uniq.length - 1]}/vehicle-day by type`;
}

export function resolveStandingCostForMergedRows(
  rows: MergedCostStandingRow[],
  options: {
    vehicleTypeByVrn?: Record<string, string>;
    depreciationByType?: Record<string, number>;
    standingPolicy?: 'calendar_day' | 'active_day';
  } = {},
): StandingCostResolution {
  const policy = options.standingPolicy ?? 'calendar_day';
  const depreciation = options.depreciationByType ?? { Default: 0 };
  const lookup = options.vehicleTypeByVrn ?? {};

  const mergedTotal = rows.reduce((s, r) => s + parseDepreciation(r), 0);
  const hasDepreciationField = rows.some(
    (r) => r.depreciation != null && String(r.depreciation).trim() !== '',
  );

  if (mergedTotal > 0 || hasDepreciationField) {
    const chargedKeys = new Set<string>();
    for (const r of rows) {
      if (parseDepreciation(r) <= 0) continue;
      const key = vehicleDayKey(r);
      if (key) chargedKeys.add(key);
    }
    const chargedVehicleDays = chargedKeys.size || rows.filter((r) => vehicleDayKey(r)).length;
    return {
      standingTotal: mergedTotal,
      chargedVehicleDays,
      averagePerDay:
        chargedVehicleDays > 0 ? Math.round((mergedTotal / chargedVehicleDays) * 100) / 100 : null,
      source: 'merged_depreciation',
      policy,
      rateSummary: summariseDepreciationRates(depreciation),
    };
  }

  let standingTotal = 0;
  const chargedKeys = new Set<string>();
  for (const r of rows) {
    const key = vehicleDayKey(r);
    if (!key || chargedKeys.has(key)) continue;
    if (policy === 'active_day' && !rowHasActivity(r)) continue;
    chargedKeys.add(key);
    const vrn = key.split('|')[0]!;
    const vehicleType = lookup[vrn] || lookup[vrn.toLowerCase()] || 'Default';
    standingTotal += resolveDepreciationRate(vehicleType, depreciation);
  }

  const chargedVehicleDays = chargedKeys.size;
  return {
    standingTotal,
    chargedVehicleDays,
    averagePerDay:
      chargedVehicleDays > 0 ? Math.round((standingTotal / chargedVehicleDays) * 100) / 100 : null,
    source: 'config_estimate',
    policy,
    rateSummary: summariseDepreciationRates(depreciation),
  };
}

export function formatMondayBriefStandingConfidence(resolution: StandingCostResolution): string {
  const policyLabel =
    resolution.policy === 'calendar_day' ? 'calendar-day' : 'active-day only';
  if (resolution.source === 'merged_depreciation') {
    return `standing cost is from computed merged-cost depreciation (profitability assumptions; ${policyLabel} policy; ${resolution.rateSummary})`;
  }
  return `standing cost is estimated from profitability assumptions (${policyLabel} policy; ${resolution.rateSummary})`;
}

export function formatVehicleStandingLine(
  standingTotal: number,
  chargedDays: number,
  averagePerDay: number | null,
  source: StandingCostResolution['source'],
): string {
  const gbp = `£${Math.round(standingTotal).toLocaleString('en-GB')}`;
  const daysLabel = chargedDays === 1 ? '1 day' : `${chargedDays} days`;
  const avg =
    averagePerDay != null && chargedDays > 0
      ? `, avg £${Math.round(averagePerDay)}/day`
      : '';
  const prefix =
    source === 'merged_depreciation'
      ? 'from profitability standing charges'
      : 'estimated from profitability assumptions';
  return `${gbp} ${prefix} across ${daysLabel}${avg}`;
}
